import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Container, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, MenuItem, Select, FormControl, InputLabel,
  TextField, Chip, OutlinedInput, Checkbox, ListItemText, Grid, Alert, Button
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const karatOptions = ['22K', '24K', '22/22K', '24/24K', '18K', 'PT95', 'WG'];

const PendingList = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [unissuing, setUnissuing] = useState(null);

  const [karigarFilter, setKarigarFilter] = useState('');
  const [karatFilter, setKaratFilter] = useState([]);
  const [requiredDateFilter, setRequiredDateFilter] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (karigarFilter) params.append('karigar', karigarFilter);
      if (karatFilter.length > 0) params.append('karat', karatFilter.join(','));
      if (requiredDateFilter) params.append('requiredDate', requiredDateFilter);

      const karigarApiUrl = user?.role === 0 ? '/api/karigar/all' : '/api/karigar';
      const [pendingRes, karigarsRes] = await Promise.all([
        api.get(`/api/karigar/pending-list?${params.toString()}`),
        api.get(karigarApiUrl)
      ]);
      setItems(pendingRes.data.items || pendingRes.data);
      setKarigars(karigarsRes.data);
    } catch (err) {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [karigarFilter, karatFilter, requiredDateFilter]);

  const handleUnissue = async (item) => {
    const key = `${item.orderId}-${item.itemId}`;
    setUnissuing(key);
    setError('');
    try {
      await api.post('/api/karigar/unissue', {
        orderId: item.orderId,
        itemId: item.itemId,
        isDirect: item.isDirect
      });
      await fetchData();
    } catch (err) {
      setError('Failed to unissue item');
    }
    setUnissuing(null);
  };

  const handleKaratChange = (event) => {
    const value = event.target.value;
    setKaratFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // If already YYYY-MM-DD BS string, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" color="primary">Back</Button>
        </Box>
        <Typography variant="h4" color="primary" sx={{ mb: 3 }}>Pending List</Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Karigar</InputLabel>
              <Select value={karigarFilter} label="Karigar" onChange={e => setKarigarFilter(e.target.value)}>
                <MenuItem value="">All Karigars</MenuItem>
                {karigars.map(k => (
                  <MenuItem key={k.abbreviation} value={k.abbreviation}>{k.abbreviation} - {k.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Karat</InputLabel>
              <Select
                multiple value={karatFilter} onChange={handleKaratChange}
                input={<OutlinedInput label="Karat" />}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(v => <Chip key={v} label={v} size="small" />)}
                  </Box>
                )}
              >
                {karatOptions.map(k => (
                  <MenuItem key={k} value={k}>
                    <Checkbox checked={karatFilter.indexOf(k) > -1} />
                    <ListItemText primary={k} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField fullWidth size="small" label="Required Date (Before)" type="date"
              value={requiredDateFilter} onChange={e => setRequiredDateFilter(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">Total: {items.length} items</Typography>
            </Box>
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Alert severity="info">No pending items found.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Bill</TableCell>
                  <TableCell>Issue Date</TableCell>
                  <TableCell>Required Date</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Qty</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Weight</TableCell>
                  <TableCell>Karat</TableCell>
                  <TableCell>Karigar</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const key = `${item.orderId}-${item.itemId}-${index}`;
                  return (
                    <TableRow key={key}>
                      <TableCell>{item.billName} {item.billNumber}</TableCell>
                      <TableCell>{formatDate(item.issueDate)}</TableCell>
                      <TableCell>{formatDate(item.requiredDate)}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell>{item.itemWeight}</TableCell>
                      <TableCell>
                        <Chip label={item.karat} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell>{item.karigarAbbreviation}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={unissuing === `${item.orderId}-${item.itemId}`}
                          onClick={() => handleUnissue(item)}
                        >
                          {unissuing === `${item.orderId}-${item.itemId}` ? 'Unissuing...' : 'Unissue'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default PendingList;
